import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { cva, VariantProps } from 'class-variance-authority';
import { cn } from '@/components/ui/utils';
import React from 'react';
import { TappIcon } from '../TappIcon';

const styles = cva('', {
  variants: {},
  defaultVariants: {},
});

export type TappMenuProps = {} & VariantProps<typeof styles> & DropdownMenu.DropdownMenuProps;

export const TappMenu: React.FC<TappMenuProps> = ({ ...props }) => {
  return <DropdownMenu.Root {...props} />;
};

export const TappMenuTrigger = React.forwardRef<HTMLButtonElement, DropdownMenu.DropdownMenuTriggerProps>(
  ({ asChild = true, ...props }, forwardedRef) => {
    return <DropdownMenu.DropdownMenuTrigger ref={forwardedRef} asChild={asChild} {...props} />;
  }
);

export const TappMenuContent = React.forwardRef<HTMLDivElement, DropdownMenu.DropdownMenuContentProps>(
  ({ className = '', children, ...props }, forwardedRef) => {
    return (
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={cn(
            'z-40 min-w-[220px] rounded bg-background py-2 text-foreground shadow-md shadow-foreground/10 border border-border dark:shadow-none',
            'origin-[var(--radix-dropdown-menu-content-transform-origin)]',
            "data-[state='closed']:animate-fadeOut data-[state='open']:animate-fadeIn",
            className
          )}
          sideOffset={8}
          ref={forwardedRef}
          {...props}
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    );
  }
);

export const TappMenuItem = React.forwardRef<
  HTMLDivElement,
  TappMenuItemContentProps & DropdownMenu.DropdownMenuItemProps
>(({ leftSlot, rightSlot, children, ...props }, forwardedRef) => {
  return (
    <DropdownMenu.Item className="group" ref={forwardedRef} {...props}>
      <TappMenuItemContent leftSlot={leftSlot} rightSlot={rightSlot}>
        {children}
      </TappMenuItemContent>
    </DropdownMenu.Item>
  );
});

export const TappMenuCheckboxItem = React.forwardRef<
  HTMLDivElement,
  Omit<TappMenuItemContentProps, 'leftSlot'> & DropdownMenu.DropdownMenuCheckboxItemProps
>(({ rightSlot, children, ...props }, forwardedRef) => {
  return (
    <DropdownMenu.CheckboxItem className="group" ref={forwardedRef} {...props}>
      <TappMenuItemContent
        leftSlot={
          // <div className="min-w-[16px]">
          <DropdownMenu.ItemIndicator className="flex leading-none">
            <TappIcon size={16}>check</TappIcon>
          </DropdownMenu.ItemIndicator>
          // </div>
        }
        rightSlot={rightSlot}
      >
        {children}
      </TappMenuItemContent>
    </DropdownMenu.CheckboxItem>
  );
});

export const TappMenuRadioGroup = React.forwardRef<HTMLDivElement, DropdownMenu.DropdownMenuRadioGroupProps>(
  ({ ...props }, forwardedRef) => {
    return <DropdownMenu.DropdownMenuRadioGroup ref={forwardedRef} {...props} />;
  }
);

export const TappMenuRadioItem = React.forwardRef<
  HTMLDivElement,
  Omit<TappMenuItemContentProps, 'leftSlot'> & DropdownMenu.DropdownMenuRadioItemProps
>(({ rightSlot, children, ...props }, forwardedRef) => {
  return (
    <DropdownMenu.RadioItem className="group" ref={forwardedRef} {...props}>
      <TappMenuItemContent
        leftSlot={
          // <div className="min-w-[16px]">
          <DropdownMenu.ItemIndicator className="flex leading-none">
            <span className="px-[2px] text-[8px] leading-none">●</span>
          </DropdownMenu.ItemIndicator>
          // </div>
        }
        rightSlot={rightSlot}
      >
        {children}
      </TappMenuItemContent>
    </DropdownMenu.RadioItem>
  );
});

export const TappMenuSubtrigger = React.forwardRef<
  HTMLDivElement,
  TappMenuItemContentProps & DropdownMenu.DropdownMenuSubTriggerProps
>(({ leftSlot, children, ...props }, forwardedRef) => {
  return (
    <DropdownMenu.SubTrigger className="group" ref={forwardedRef} {...props}>
      <TappMenuItemContent
        leftSlot={leftSlot}
        rightSlot={<TappIcon className="text-base leading-none">chevron_right</TappIcon>}
      >
        {children}
      </TappMenuItemContent>
    </DropdownMenu.SubTrigger>
  );
});

type TappMenuItemContentProps = {
  children?: React.ReactNode;
  rightSlot?: React.ReactNode;
  leftSlot?: React.ReactNode;
};

const TappMenuItemContent = React.forwardRef<HTMLDivElement, { className?: string } & TappMenuItemContentProps>(
  ({ className = '', leftSlot, rightSlot, children }, forwardedRef) => {
    return (
      <div
        ref={forwardedRef}
        className={cn(
          'relative flex h-8 cursor-pointer items-center py-2 pl-4 pr-4 text-xs transition group-data-[highlighted]:bg-muted group-data-[state="open"]:bg-muted',
          className
        )}
      >
        {leftSlot && (
          <div className="flex items-center justify-center pr-2 text-muted-foreground empty:pr-0 group-data-[disabled]:text-muted-foreground">
            {leftSlot}
          </div>
        )}
        <span className="font-medium leading-5 text-foreground group-data-[disabled]:text-muted-foreground">
          {children}
        </span>{' '}
        {rightSlot && (
          <div className="ml-auto flex items-center justify-center pl-6 text-gray-500 empty:pl-0 group-data-[disabled]:text-gray-300">
            {rightSlot}
          </div>
        )}
      </div>
    );
  }
);

export const TappMenuSub: React.FC<DropdownMenu.DropdownMenuSubProps> = ({ ...props }) => {
  return <DropdownMenu.DropdownMenuSub {...props} />;
};

export const TappMenuSubContent = React.forwardRef<HTMLDivElement, DropdownMenu.DropdownMenuSubContentProps>(
  ({ className = '', children, ...props }, forwardedRef) => {
    return (
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          className={cn(
            'z-40 min-w-[220px] rounded bg-background py-2 shadow-md shadow-foreground/10 border border-border dark:shadow-none',
            'origin-[var(--radix-dropdown-menu-content-transform-origin)]',
            "data-[state='closed']:animate-fadeOut data-[state='open']:animate-fadeIn",
            className
          )}
          ref={forwardedRef}
          sideOffset={-4}
          alignOffset={-8}
          {...props}
        >
          {children}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    );
  }
);

export const TappMenuSeparator = React.forwardRef<HTMLDivElement, DropdownMenu.DropdownMenuSeparatorProps>(
  ({ ...props }, forwardedRef) => {
    return <DropdownMenu.Separator ref={forwardedRef} className="my-2 h-px bg-muted" {...props} />;
  }
);

export const TappMenuLabel = React.forwardRef<HTMLDivElement, DropdownMenu.DropdownMenuLabelProps>(
  ({ children, ...props }, forwardedRef) => {
    return (
      <DropdownMenu.Label ref={forwardedRef} className="mb-1 ml-4 text-xs font-normal text-muted-foreground">
        {children}
      </DropdownMenu.Label>
    );
  }
);

export const TappMenuArrow = React.forwardRef<SVGSVGElement, DropdownMenu.DropdownMenuArrowProps>(
  ({ ...props }, forwardedRef) => {
    return (
      <DropdownMenu.DropdownMenuArrow
        ref={forwardedRef}
        className="fill-background shadow-md dark:shadow-none"
        {...props}
      />
    );
  }
);
